import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'

// Same threshold utils/isMobile uses, so what the routes call "mobile" and what
// these styles call "mobile" are the one thing. Everything below is additive:
// above this width nothing here applies and the desktop layout is untouched.
const MOBILE = '@media (max-width: 768px)'
import { Dialog, DialogTitle, DialogContent, FormControlLabel } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Edit, Delete, Add, Close, Phone } from '@material-ui/icons'
import { 
    Button, 
    TextField,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer,
    TableRow, 
    TableHead, 
    RadioGroup, 
    Radio, 
    Checkbox, 
    IconButton, 
    Select, 
    InputLabel, 
    MenuItem 
} from '@material-ui/core' 

export const FilterButtonApply = styled(Button)`
  display: inline-flex;
  padding: 7.815px 15.994px 8.179px 15.994px;
  justify-content: center;
  align-items: flex-end;
  gap: 7.386px;
  opacity: 0.5;
`

export const StyledInputContainer = styled.div`
  display: flex;
  flex: 0 1 calc(25% - 12px);
  min-width: 200px;
  flex-direction: column;
  gap: 6px;
`
export const StyledInput = styled.input`
  display: flex;
  width: 100%;
  min-width: 250px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 6px;
  border: 0.909px solid #E2E8F0;
  background: #F8FAFC;
`
export const StyledLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #364153;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  text-align: right;
`
export const StyledRequired = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; 
`
export const StyledSelect = styled.select`
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  font-family: Rubik;
  font-size: 14px;
  color: #0F172A;
  outline: none;
  cursor: pointer;
  text-align: right;
  &:focus { border-color: #6AA2FD; }
`

// Header bar 
export const HeaderImageIcon = styled(Image)`
  width: 16px;
  height: 16px;
`
export const Header = styled.div` 
  display: flex;
  height: 67.983px;
  padding-right: 0;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    gap: 12px;
  }
`
export const HeaderButtonGroup = styled.div`
  display: flex;
  height: 41.804px;
  align-items: flex-start;
  gap: 11.989px;

  ${MOBILE} {
    height: auto;
    flex-wrap: wrap;
    gap: 8px;
  }
`
export const HeaderTitleContainer = styled.div`
  display: flex;
  height: 67.983px;
  flex-direction: column;
  align-items: flex-start;
  gap: 7.997px;
  flex: 1 0 0;

  ${MOBILE} {
    height: auto;
    flex: 0 0 auto;
  }
`
export const HeaderMainTitle = styled.div`
  flex: 1 0 0;
  color: #101828;
  text-align: right;
  font-family: Rubik;
  font-size: 30px;
  font-style: normal;
  font-weight: 500;
  line-height: 36px;

  ${MOBILE} {
    flex: 0 0 auto;
    font-size: 22px;
    line-height: 28px;
  }
`
export const HeaderSecondTitle = styled.div`
  color: #4C5564;
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`
export const HeaderButton = styled(Button)`
  display: flex;
  padding: 9.634px 16.903px 8.17px 16.903px;
  justify-content: center;
  align-items: center;
  gap: 7.611px;
  border-radius: 8px;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; 
  border-radius: 8px;
  border: 1px solid ${p => p.bcolor};
  color: ${p => p.color};
  background: ${p => p.bg};
`

// Starts Bar
export const StatsBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  margin-top: 15px;
  /* Keep full height when the page is zoomed in - the page scrolls instead. */
  flex-shrink: 0;

  ${MOBILE} {
    /* Five fixed-width cards cannot fit a phone. Rather than shrink them to
       nothing, the row scrolls sideways and every card stays one tap away. */
    justify-content: flex-start;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`
export const StatsCard = styled.div`
  display: flex;
  width: 287.997px;
  height: 87.628px;
  padding: 17.813px 17.813px 1.818px 17.813px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 2px solid ${p => p.border};
  background: #FFF;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);
  cursor: pointer;

  ${MOBILE} {
    width: 172px;
    flex: 0 0 172px;
  }
`
export const StatsCardContainer = styled.div`
  display: flex;
  height: 52.003px;
  padding-right: 0;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
`
export const StatsTitleContainer = styled.div`
  display: flex;
  height: 52.003px;
  flex-direction: column;
  align-items: flex-start;
`
export const StatsTitle = styled.div`
  color: #4C5564;
  text-align: right;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`
export const StatsCount = styled.div`
  display: flex;
  padding-left: 15.028px;
  flex-direction: column; 
  justify-content: center;
  align-items: center;
  align-self: stretch;
  color: ${p => p.color};
  text-align: right;
  font-family: Rubik;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
`
export const StatsImageIcon = styled(Image)`
  width: 32px;
  height: 32px;
  color: ${p => p.color};
`
export const StatusItemImageIcon = styled(Image)`
  width: 16px;
  height: 16px;
  color: ${p => p.color};
`

// Search Bar
export const SearchAndFilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin-top: 15px;
  border-radius: 12px;
  border: 0.909px solid #E5E7EB;
  background: #FFF;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04);
  /* Never squeeze the advanced filter panel - the page scrolls instead, so
     every field stays reachable at 125%+ browser zoom. */
  flex-shrink: 0;
  /* Not 'overflow: hidden': the date-range calendar is an absolutely
     positioned popup that hangs below its input, and hiding the overflow cut
     it off mid-month. The rounded corners come from the children instead. */
  overflow: visible;

  & > *:first-child {
    border-radius: 12px 12px 0 0;
  }
  & > *:last-child {
    border-radius: 0 0 12px 12px;
  }
  & > *:only-child {
    border-radius: 12px;
  }
`

export const SearchBar = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  align-self: stretch;
  background: #FFF;
`
export const SearchBarContainer = styled.div`
  display: flex;
  justify-content: space-between; 
  align-items: center;
  align-self: stretch;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`
export const SearchInput = styled(TextField)`
  display: flex;
  margin-left: 10px; 
  width: 100%;
  border-radius: 6px;
  border: 0.909px solid #E2E8F0;
  height: 45.81px;
  background: #F8FAFC;
  align-items: center;
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  .MuiOutlinedInput-root {
    padding: 0px;
    width 100%;
    height: 46px;
  }

  .MuiOutlinedInput-notchedOutline{
    border: 0px;
  }
`
export const FilterButton = styled(Button)`
  display: flex;
  width: 151.349px;
  height: 45.81px;
  justify-content: center;
  align-items: center;
  gap: 7.997px;
  border-radius: 8px;
  border: 0.909px solid ${p => p.active ? '#8EC5FF' : '#D1D5DC'};
  color: ${p => p.active ? '#1447E6' : '#000000'};
  background: ${p => p.active ? '#EFF6FF' : '#FFF'};
`
// Filter bar
export const FilterImageIcon = styled(Image)`
  color: ${p => p.active ? '#1447E6' : '#000000'};
  width: 16px;
  height: 16px;
`
export const FilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;
  width: 100%;
  padding: 20px 24px 24px;
  box-sizing: border-box;
  border-top: 0.909px solid #E5E7EB;
  background: #FFF;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }
`

export const FilterSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0F172A;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  text-align: right;
`
export const FilterTitle = styled(Button)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #364153;
  text-align: right;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`
export const FilterRadioContainer = styled.div`
  display: flex;
  /* min-height, not height: the two radio labels wrap onto a second line on
     narrow viewports / high zoom and must not be clipped. */
  min-height: 20px;
  align-items: flex-start;
  flex-shrink: 0;
  align-self: stretch;

  & .MuiFormGroup-root {
    gap: 16px;
  }
  & .MuiFormGroup-root > label {
    margin-left: 0;
    margin-right: 0;
  }
`
export const FilterRadioFormControlLabel = styled(FormControlLabel)`
  & .MuiIconButton-root {
    width: 14px;
    height: 14px;
    margin-left: 3px;
  }

  & .MuiTypography-body1 {
    color: #0F172A;
    text-align: right;
    font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
  }
`
export const FilterRadioSelectedTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  color: #6A7282;
  text-align: right;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`
export const FilterInputContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  align-items: stretch;
  gap: 16px;
  flex-wrap: wrap;
`
export const FilterTitleContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  ${p => p.fullWidth ? 'grid-column: 1 / -1;' : ''}
`

// A single field cell (label + control) that fills its grid column.
export const FilterField = styled.div`
  display: flex;
  min-width: 0;
  flex: 1 1 0;
  flex-direction: column;
  gap: 6px;
`

// Two date inputs side by side inside one grid cell; stack on narrow screens.
export const DateRangeRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  align-items: stretch;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

// Full-width row that hosts the "clear filters" action and the active-filter
// count that the spec asks for at the bottom of the panel.
export const FilterActionsRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const FilterCountLabel = styled.div`
  color: #6A7282;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`

// Table bar
export const TableBar = styled.div`
  display: flex;
  margin-top: 15px;
  flex: 1 1 auto;
  /* Floor the table height so it can't be crushed to nothing when the rest of
     the page grows (browser zoom); past this the page scrolls. */
  min-height: 280px;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  align-self: stretch;

  /* RTL: MUI's TablePagination doesn't auto-flip its next/prev icons
     unless wrapped in an RTL theme, so we flip the SVGs here. */
  & .MuiTablePagination-actions svg {
    transform: rotate(180deg);
  }

  /* The pagination row keeps its full height when vertical space runs short
     (browser zoom); the table above it scrolls instead of the row being cut. */
  & .MuiTablePagination-root {
    flex-shrink: 0;
  }

  ${MOBILE} {
    margin-top: 10px;
    & .MuiTablePagination-toolbar {
      flex-wrap: wrap;
      row-gap: 4px;
      padding-left: 0;
      padding-right: 0;
    }
  }
`
export const TableContainerStyled = styled(TableContainer)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  ${MOBILE} {
    /* Ten columns will not fit a phone. Scrolling sideways keeps every column
       readable and every row complete; squeezing them would do neither. The
       min-width is what gives the scroll something to travel over. */
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    & table {
      min-width: 900px;
    }
  }
`
export const TableHeadStyled = styled(TableHead)`
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 0.909px solid #E2E8F0;
  background: rgba(241, 245, 249, 0.95);
`
export const TableCellTitle = styled.div`
  color: #364153;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`
export const TableCellCategory = styled.div`
  display: flex;
  height: 25.795px;
  width: fit-content;
  max-width: 160px;
  margin: 0 auto;
  padding: 4.81px 8.636px 4.986px 8.906px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  border: 1px solid ${p => p.color};
  color: ${p => p.color};
  background: ${p => p.bg};  
`
// The label is its own box so it can be cut: a long category name used to wrap
// inside the chip and spill past its border. 'min-width: 0' is what lets a flex
// item shrink below its text - without it the chip grows instead.
export const TableCellCategoryLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
export const TableCelllOnline = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #155DFC;
`
// A title or topic with no spaces in it - an imported one often has none - has
// nothing to wrap on, so the cell grows until it runs over the columns beside
// it. Cap the text and cut it with an ellipsis instead. The cap is on the text
// itself rather than on the cell: the table lays out automatically, where a
// max-width on a cell is not honoured.
const truncatedLine = `
  max-width: min(40vw, 560px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const TableCellDescription1 = styled.div`
  color: ${p => p.color};
  font-family: Rubik; 
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  ${truncatedLine}
`
export const TableCellDescription2 = styled.div`
  color: #6A7282;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  ${truncatedLine}
`
// 'start', not a fixed side: the column follows the UI language, so it reads
// from the right in Hebrew and from the left once the app is switched to a
// left-to-right language. ('align-items: right' here was a no-op - align-items
// does not apply to a table cell, and 'right' is not one of its values.)
export const TableCellStyled = styled(TableCell)`
  text-align: start;
`

// Short values - badges and counts - sit centred under their header instead.
export const TableCellCentered = styled(TableCellStyled)`
  text-align: center;
`

export const HeadCell = styled(TableCell)`
  text-align: start;
`

export const HeadCellCentered = styled(TableCell)`
  text-align: center;
`
export const TableCellStatus = styled.div`
  display: flex;
  width: fit-content;
  height: 25.795px;
  margin: 0 auto;
  padding: 5px 9px 5px 9px;
  justify-content: center;
  align-items: center;
  gap: 3.585px;
  border-radius: 30504000px;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  border: 1px solid ${p => p.color};
  color: ${p => p.color};
  background: ${p => p.bg};
`